import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Package, DollarSign, Hash, Grid, List } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Product, Currency, ProductCategory } from '../types';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';

const ProductsList: React.FC = () => {
  const { products, productCategories, addProduct, updateProduct, deleteProduct, addProductCategory } = useData();
  const { currentCompany } = useTenant();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [localCategories, setLocalCategories] = useState<ProductCategory[]>([]);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    currency: 'CLP' as 'CLP' | 'USD' | 'CLF',
    unit_value: '',
    category_id: ''
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    code: '',
    description: ''
  });

  // Filter products by current company
  const companyProducts = products.filter(product => product.company_id === currentCompany?.id);
  // Mostrar todas las categorías disponibles (no filtrar por empresa)
  const companyCategories = localCategories;

  useEffect(() => {
    loadCurrencies();
    if (currentCompany) {
      loadProductCategories();
    }
  }, [currentCompany]);

  useEffect(() => {
    // Cargar categorías si no se han cargado automáticamente
    if (productCategories.length === 0 && currentCompany) {
      loadProductCategories();
    }
  }, [currentCompany, productCategories.length]);

  const loadProductCategories = async () => {
    if (!currentCompany) return;
    
    try {
      // Primero, intentar cargar categorías de la empresa actual
      const { data: companyData } = await supabase
        .from('product_categories')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('name');

      if (companyData && companyData.length > 0) {
        setLocalCategories(companyData);
        return;
      }

      // Si no hay categorías para la empresa actual, buscar en todas las empresas
      const { data: allData, error: allError } = await supabase
        .from('product_categories')
        .select('*')
        .order('name');

      if (allError) {
        console.error('Error loading product categories:', allError);
        return;
      }

      setLocalCategories(allData || []);
    } catch (error) {
      console.error('Error loading product categories:', error);
    }
  };

  const loadCurrencies = async () => {
    try {
      const { data, error } = await supabase
        .from('currencies')
        .select('*')
        .in('code', ['CLP', 'USD', 'CLF'])
        .eq('is_active', true)
        .order('code');

      if (error) throw error;
      setCurrencies(data || []);
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  };

  const filteredProducts = companyProducts.filter(product => {
    const matchesSearch = product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCurrency = !currencyFilter || product.currency === currencyFilter;
    return matchesSearch && matchesCurrency;
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const productData = {
      code: formData.code,
      description: formData.description,
      currency: formData.currency,
      unit_value: parseFloat(formData.unit_value),
      category_id: formData.category_id,
      createdAt: new Date().toISOString() // Add createdAt field for compatibility
    };

    if (editingProduct) {
      updateProduct(editingProduct.id, productData);
    } else {
      addProduct(productData);
    }
    resetForm();
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code,
      description: product.description,
      currency: product.currency,
      unit_value: product.unit_value.toString(),
      category_id: product.category_id || ''
    });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      deleteProduct(id);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      currency: 'CLP',
      unit_value: '',
      category_id: ''
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const formatCurrency = (value: number, currency: string) => {
    if (currency === 'CLF') {
      return `CLF ${value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
    } else if (currency === 'USD') {
      return `USD ${value.toLocaleString('es-CL', { minimumFractionDigits: 2 })}`;
    } else {
      return `CLP ${value.toLocaleString('es-CL')}`;
    }
  };

  const getCurrencyLabel = (currency: string) => {
    const currencyObj = currencies.find(c => c.code === currency);
    return currencyObj ? `${currencyObj.code} (${currencyObj.name})` : currency;
  };

  // Función para generar código automático de producto
  const generateProductCode = async (categoryId: string): Promise<string> => {
    if (!currentCompany) return '';
    
    try {
      const category = localCategories.find(c => c.id === categoryId);
      if (!category) return '';

      // Usar la función RPC de Supabase
      const { data, error } = await supabase
        .rpc('generate_product_code', {
          category_code: category.code,
          company_id: currentCompany.id
        });

      if (error) {
        console.error('Error generando código con RPC:', error);
        return '';
      }

      return data || '';
      
    } catch (error) {
      console.error('Error generating product code:', error);
      return '';
    }
  };

  // Función para obtener el nombre de la categoría
  const getCategoryName = (categoryId: string) => {
    return localCategories.find(c => c.id === categoryId)?.name || 'Sin categoría';
  };

  // Funciones para manejar categorías
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      // TODO: Implementar actualización de categoría
    } else {
      const category = await addProductCategory(categoryFormData);
      if (category) {
        resetCategoryForm();
      }
    }
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      code: '',
      description: ''
    });
    setEditingCategory(null);
    setShowCategoryForm(false);
  };

  // Función para manejar cambio de categoría en el formulario de producto
  const handleCategoryChange = async (categoryId: string) => {
    setFormData(prev => ({ ...prev, category_id: categoryId }));
    
    // Generar código automático si no estamos editando
    if (!editingProduct && categoryId) {
      const generatedCode = await generateProductCode(categoryId);
      if (generatedCode) {
        setFormData(prev => ({ ...prev, code: generatedCode }));
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Productos</h1>
          <p className="text-gray-600">Gestiona el catálogo de productos y servicios de {currentCompany?.name}</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Vista de grilla"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Vista de lista"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          
          <button
            onClick={() => setShowCategoryForm(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center"
          >
            <Package className="h-5 w-5 mr-2" />
            Categorías
          </button>
          
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nuevo Producto
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  readOnly={!editingProduct}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    !editingProduct ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="Ej: TELC001"
                />
                {!editingProduct && (
                  <p className="text-xs text-gray-500 mt-1">El código se genera automáticamente al seleccionar una categoría</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoría *
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar categoría</option>
                  {companyCategories.length > 0 ? (
                    companyCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.code})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No hay categorías disponibles</option>
                  )}
                </select>
                {companyCategories.length === 0 && (
                  <div className="text-xs text-red-500 mt-1">
                    <p>No hay categorías creadas. Crea una categoría primero.</p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setShowCategoryForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 underline mt-1"
                    >
                      Crear categoría ahora
                    </button>
                  </div>
                )}
                {companyCategories.length > 0 && companyCategories.every(c => c.company_id !== currentCompany?.id) && (
                  <div className="text-xs text-yellow-600 mt-1">
                    <p>Las categorías disponibles son de otras empresas. Considera crear categorías específicas para tu empresa.</p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setShowCategoryForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 underline mt-1"
                    >
                      Crear categoría para esta empresa
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Descripción del producto o servicio"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Moneda *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value as any })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Unitario *
                </label>
                <input
                  type="number"
                  value={formData.unit_value}
                  onChange={(e) => setFormData({ ...formData, unit_value: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingProduct ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
              </h2>
              <button
                onClick={resetCategoryForm}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ej: Telecomunicaciones"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  value={categoryFormData.code}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, code: e.target.value.toUpperCase() })}
                  required
                  maxLength={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ej: TELC"
                />
                <p className="text-xs text-gray-500 mt-1">Máximo 10 caracteres. Se usará para generar códigos de productos.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Descripción de la categoría"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetCategoryForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingCategory ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar productos por código o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas las monedas</option>
              {currencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Display */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="p-2 bg-green-100 rounded-lg mr-3">
                      <Package className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center mb-1">
                        <Hash className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {product.code}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                        {product.description}
                      </h3>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(product)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="h-4 w-4 mr-2 text-green-600" />
                      <span className="text-gray-500">Valor Unitario:</span>
                    </div>
                    <span className="font-bold text-green-600 text-lg">
                      {formatCurrency(product.unit_value, product.currency)}
                    </span>
                  </div>

                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Categoría:</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {getCategoryName(product.category_id || '')}
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Moneda:</span>
                      <span 
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.currency === 'CLF' ? 'bg-purple-100 text-purple-800' :
                          product.currency === 'USD' ? 'bg-green-100 text-green-800' :
                          'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {getCurrencyLabel(product.currency)}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-400">
                    Creado: {new Date(product.created_at).toLocaleDateString('es-ES')}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full">
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay productos</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || currencyFilter ? 'No se encontraron productos con los filtros aplicados.' : 'Comienza agregando tu primer producto.'}
              </p>
              {!searchTerm && !currencyFilter && (
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Nuevo Producto
                </button>
              )}
            </div>
          </div>
        )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Producto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Unitario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Moneda
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha Creación
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-green-100 rounded-lg mr-3">
                            <Package className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                            {product.description}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {product.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {getCategoryName(product.category_id || '')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-bold text-green-600">
                          {formatCurrency(product.unit_value, product.currency)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            product.currency === 'CLF' ? 'bg-purple-100 text-purple-800' :
                            product.currency === 'USD' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {getCurrencyLabel(product.currency)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(product.created_at).toLocaleDateString('es-ES')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(product)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No hay productos</h3>
                      <p className="text-gray-600 mb-6">
                        {searchTerm || currencyFilter ? 'No se encontraron productos con los filtros aplicados.' : 'Comienza agregando tu primer producto.'}
                      </p>
                      {!searchTerm && !currencyFilter && (
                        <button
                          onClick={() => setShowForm(true)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Nuevo Producto
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsList;